import React, { useState, useEffect, useCallback } from "react"
import { useNavigate } from 'react-router-dom'
import SelectUnscannedChannelsModal from "./ChannelsModal"
import axios from "axios"
import { ENV } from "../../../utils/env.js"
import { useToaster } from "../../../contexts/ToasterContext.js"
import { ArrowLeft, Filter, Eye, EyeOff, Search, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardFooter } from '../../../components/ui/card'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../../../components/ui/breadcrumb'

// SlackFindingsDisplay Component (similar to S3 FindingsDisplay)
const SlackFindingsDisplay = ({ 
  channel, 
  findings, 
  loading, 
  onBack, 
  selectedChannel, 
  fetchChannelFindings,
  pagination,
  handlePrevPage,
  handleNextPage,
  handlePerPageChange,
  filterType,
  setFilterType,
  revealed,
  setRevealed,
  showToast,
  resolvingAlerts,
  setResolvingAlerts,
  setFindings
}) => {
  // Bulk selection state and row error tracking (consistent with other flows)
  const [selected, setSelected] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [bulkResolveLoading, setBulkResolveLoading] = useState(false);
  // Filtering logic - ensure findings is an array and handle both field cases
  const findingsArray = Array.isArray(findings) ? findings : []
  let filteredFindings = findingsArray
  if (filterType === 'verified') {
    filteredFindings = findingsArray.filter(f => f.Verified || f.verified === true)
  } else if (filterType === 'unverified') {
    filteredFindings = findingsArray.filter(f => !(f.Verified || f.verified))
  } else if (filterType === 'resolved') {
    filteredFindings = findingsArray.filter(f => f.Resolved || f.resolved)
  } else if (filterType === 'unresolved') {
    filteredFindings = findingsArray.filter(f => !(f.Resolved || f.resolved))
  }
  
  // Sort verified findings first when 'all' is selected
  if (filterType === 'all' && Array.isArray(filteredFindings)) {
    filteredFindings.sort((a, b) => {
      const aVerified = a.Verified || a.verified
      const bVerified = b.Verified || b.verified
      if (aVerified && !bVerified) return -1;
      if (!aVerified && bVerified) return 1;
      return 0;
    });
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 bg-white border border-gray-300 text-black px-4 py-2 rounded-xl hover:bg-gray-50 transition-all duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Slack Channels
          </button>
          <h2 className="text-2xl font-bold text-black">{channel?.name} - Security Findings</h2>
        </div>
        <div className="text-center py-12">
          <p className="text-black text-lg mb-4">Loading findings...</p>
        </div>
      </div>
    )
  }

  if (findingsArray.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 bg-white border border-gray-300 text-black px-4 py-2 rounded-xl hover:bg-gray-50 transition-all duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Slack Channels
          </button>
          <h2 className="text-2xl font-bold text-black">{channel?.name}</h2>
        </div>
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-black mb-2">No Findings Yet</h3>
          <p className="text-gray-600 mb-4">
            The security scan for this channel is still in progress or no vulnerabilities were found.
          </p>
          <p className="text-sm text-gray-500">Please come back later after a few hours to check for results.</p>
        </div>
      </div>
    )
  }

  // Helpers to derive stable row id and selection utilities
  const getAlertId = (alert, index) => String(alert.ID || alert.id || index);
  const visibleIds = (filteredFindings || []).map((f, i) => getAlertId(f, i));
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected[id]);
  const selectedCount = Object.entries(selected).filter(([id, v]) => v && visibleIds.includes(id)).length;

  const selectedAlerts = () => (filteredFindings || []).filter((f, i) => selected[getAlertId(f, i)]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (prev[id] && !next[id]) {
        setRowErrors((errs) => {
          const n = { ...errs };
          delete n[id];
          return n;
        });
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected((prev) => {
        const next = { ...prev };
        visibleIds.forEach((id) => { delete next[id]; });
        return next;
      });
      clearRowErrors(visibleIds);
    } else {
      setSelected((prev) => {
        const next = { ...prev };
        visibleIds.forEach((id) => { next[id] = true; });
        return next;
      });
    }
  };

  const clearRowErrors = (rowIds = null) => {
    if (rowIds === null) {
      setRowErrors({});
    } else {
      setRowErrors((prev) => {
        const next = { ...prev };
        rowIds.forEach((id) => delete next[id]);
        return next;
      });
    }
  };

  // Bulk resolve alerts
  const handleBulkResolveAlerts = async () => {
    const items = selectedAlerts();
    if (items.length === 0) return;
    setBulkResolveLoading(true);
    clearRowErrors(items.map((a, i) => getAlertId(a, i)));

    const errors = {};
    let successCount = 0;

    for (let i = 0; i < items.length; i++) {
      const alert = items[i];
      const alertId = alert.ID || alert.id;
      try {
        const response = await axios.post(
          `${ENV.REACT_APP_URL}api/slack/alerts/${alertId}/resolve`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt")}`
            }
          }
        );

        if (response.data?.message?.includes('resolved successfully')) {
          successCount++;
          setFindings((prev) => prev.map((f) => ((f.ID || f.id) === alertId ? { ...f, Resolved: true, resolved: true } : f)));
        } else if (response.data?.message === 'Alert is already resolved') {
          successCount++;
          setFindings((prev) => prev.map((f) => ((f.ID || f.id) === alertId ? { ...f, Resolved: true, resolved: true } : f)));
        } else {
          errors[alertId] = response.data?.message || 'Failed to resolve';
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Failed to resolve';
        errors[alertId] = msg;
      }
    }

    setRowErrors(errors);
    setBulkResolveLoading(false);

    if (successCount > 0) {
      const idsToClear = items.map((a, i) => getAlertId(a, i));
      setSelected((prev) => {
        const next = { ...prev };
        idsToClear.forEach((id) => { delete next[id]; });
        return next;
      });
      showToast(`Successfully resolved ${successCount} alert(s)`, 'success');
    }
    const failCount = Object.keys(errors).length;
    if (failCount > 0) {
      showToast(`${failCount} alert(s) failed. Check individual rows for details.`, 'error');
    }
  };

  return (
      <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
            className="flex items-center gap-2 bg-white border border-gray-300 text-black px-4 py-2 rounded-xl hover:bg-gray-50 transition-all duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Slack Channels
        </button>
        <h2 className="text-2xl font-bold text-black">{channel?.name} - Security Findings</h2>
      </div>

      {/* Findings Table */}
      <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
        {/* Filter Section */}
        <div className="py-3 border-b border-gray-200 sticky top-0 z-10 bg-white px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-[#000000]" />
              <span className="text-[#000000] text-sm">Filter by:</span>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilterType('all')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  filterType === 'all'
                    ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                    : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                }`}
              >
                All ({findingsArray.length})
              </button>
              <button
                onClick={() => setFilterType('verified')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  filterType === 'verified'
                    ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                    : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                }`}
              >
                Active ({findings.filter(f => f.Verified || f.verified).length})
              </button>
              <button
                onClick={() => setFilterType('unverified')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  filterType === 'unverified'
                    ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                    : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                }`}
              >
                Unvalidated ({findings.filter(f => !(f.Verified || f.verified)).length})
              </button>
              <button
                onClick={() => setFilterType('resolved')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  filterType === 'resolved'
                    ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                    : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                }`}
              >
                Resolved ({findings.filter(f => f.Resolved || f.resolved).length})
              </button>
              <button
                onClick={() => setFilterType('unresolved')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  filterType === 'unresolved'
                    ? 'bg-[#441a9a] text-white border border-[#441a9a] hover:bg-[#3a1080]'
                    : 'bg-white text-[#441a9a] border border-[#441a9a] hover:bg-[#f3effd]'
                }`}
              >
                Unresolved ({findings.filter(f => !(f.Resolved || f.resolved)).length})
              </button>
            </div>
            {/* Bulk actions inline with filter bar */}
            {selectedCount > 0 && (
              <div className="flex items-center gap-2 rounded border border-[#C0ACF3] bg-[#F4F0FD] px-2 py-1">
                <span className="text-xs text-[#000000]">Selected {selectedCount}</span>
                <button
                  onClick={handleBulkResolveAlerts}
                  disabled={bulkResolveLoading}
                  className={`px-3 py-1 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1`}
                >
                  {bulkResolveLoading && (
                    <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent"></div>
                  )}
                  Resolve Alerts
                </button>
              </div>
            )}
            </div>
          </div>
        </div>

        <div className="p-6">
          {loading && filteredFindings.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-black text-lg mb-4">
                {filterType === 'all' 
                  ? 'No findings found for this channel'
                  : `No ${filterType==='verified'? "active" : "unvalidated"} findings found for this channel`
                }
              </p>
              <p className="text-black text-sm">
                {filterType === 'all' 
                  ? 'This channel appears to be clean of any detected secrets.'
                  : `Try changing the filter to see other findings.`
                }
              </p>
            </div>
          ) : (
            <div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left p-3 text-[#000000] font-medium w-10">
                        <input
                          type="checkbox"
                          aria-label="Select all"
                          className="h-4 w-4 rounded border-[#C0ACF3] text-[#6B5FB3] focus:ring-[#6B5FB3] align-middle"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th className="text-left p-3 text-[#000000] font-medium">Status</th>
                      <th className="text-left p-3 text-[#000000] font-medium">Resolved</th>
                      <th className="text-left p-3 text-[#000000] font-medium">Alert ID</th>
                      <th className="text-left p-3 text-[#000000] font-medium">User</th>
                      <th className="text-left p-3 text-[#000000] font-medium">Source</th>
                      <th className="text-left p-3 text-[#000000] font-medium">Raw Result</th>
                      <th className="text-left p-3 text-[#000000] font-medium">Detection Time</th>
                      <th className="text-left p-3 text-[#000000] font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFindings.map((alert, index) => {
                      const isVerified = alert.Verified || alert.verified
                      const isResolved = alert.Resolved || alert.resolved
                      const alertId = alert.ID || alert.id
                      const username = alert.Username || alert.username
                      const source = alert.Source || alert.source
                      const secret = alert.Secret || alert.secret
                      const timestamp = alert.Timestamp || alert.timestamp
                      const rowId = getAlertId(alert, index)
                      const isSelected = !!selected[rowId]
                      const hasError = !!rowErrors[rowId]
                      
                      const handleResolveToggle = async () => {
                        if (resolvingAlerts.has(alertId)) return; // Prevent multiple clicks
                        
                        setResolvingAlerts(prev => new Set([...prev, alertId]));
                        
                        try {
                          const response = await axios.post(
                            `${ENV.REACT_APP_URL}api/slack/alerts/${alertId}/resolve`,
                            {},
                            {
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${localStorage.getItem("jwt")}`,
                              },
                            }
                          )
                          
                          console.log('Resolve response:', response.data)
                          
                          // Handle successful resolution
                          if (response.data.message && response.data.message.includes('resolved successfully')) {
                            // Update the local state to reflect the resolved status
                            setFindings(prevFindings => 
                              prevFindings.map(finding => 
                                (finding.ID || finding.id) === alertId 
                                  ? { ...finding, Resolved: true, resolved: true }
                                  : finding
                              )
                            )
                            
                            // Show success message
                            showToast(response.data.message, 'success')
                          } else if (response.data.message === 'Alert is already resolved') {
                            showToast('This alert is already resolved', 'info')
                          }
                        } catch (error) {
                          console.error('Error toggling resolved status:', error)
                          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update resolved status'
                          showToast(errorMessage, 'error')
                        } finally {
                          setResolvingAlerts(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(alertId);
                            return newSet;
                          });
                        }
                      }
                      
                      return (
                        <React.Fragment key={alertId || index}>
                        <tr className={`border-b border-gray-100 hover:bg-gray-50 ${isSelected ? 'bg-[#E9E3FB]' : ''} ${hasError ? 'bg-red-50 border-red-200' : ''}`}>
                          <td className="p-3 align-middle text-center">
                            <input
                              type="checkbox"
                              aria-label={`Select alert ${rowId}`}
                              className="h-4 w-4 rounded border-[#C0ACF3] text-[#6B5FB3] focus:ring-[#6B5FB3] align-middle"
                              checked={isSelected}
                              onChange={() => toggleSelect(rowId)}
                            />
                          </td>
                          <td className="p-3">
                            <div className="flex items-center space-x-2">
                              <div className={`w-3 h-3 rounded-full ${
                                isVerified ? 'bg-green-500' : 'bg-gray-400'
                              }`}></div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                isVerified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {isVerified ? 'Active' : 'Unvalidated'}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center space-x-2">
                              <div className={`w-3 h-3 rounded-full ${
                                isResolved ? 'bg-blue-500' : 'bg-gray-400'
                              }`}></div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                isResolved ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {isResolved ? 'Resolved' : 'Unresolved'}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            <span className="font-mono text-xs text-gray-600">
                              {alertId ? alertId.toString().slice(0, 8) : 'N/A'}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center space-x-2">
                              <div className="w-6 h-6 bg-[#C0ACF3] rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <span className="text-black font-medium">{username || 'Unknown User'}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              source === 'AWS_ACCESS_KEY' ? 'bg-red-100 text-red-700' :
                              source === 'API_KEY' ? 'bg-yellow-100 text-yellow-700' :
                              source === 'PASSWORD' ? 'bg-orange-100 text-orange-700' :
                              source === 'Slack' ? 'bg-purple-100 text-purple-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {source}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-gray-900 break-words max-w-xs" style={{wordBreak: 'break-all'}}>
                            <div className="flex items-center">
                              <span className="font-mono">
                                {secret
                                  ? revealed[`raw_result-${selectedChannel}-${index}`]
                                    ? secret
                                    : `${secret.slice(0, 6)}...`
                                  : 'N/A'}
                              </span>
                              {secret && (
                                <button
                                  className="ml-2 text-gray-400 hover:text-gray-700 focus:outline-none"
                                  title={revealed[`raw_result-${selectedChannel}-${index}`] ? 'Hide' : 'Show'}
                                  onClick={() => {
                                    const key = `raw_result-${selectedChannel}-${index}`;
                                    const newRevealed = { ...revealed, [key]: !revealed[key] };
                                    setRevealed(newRevealed);
                                    localStorage.setItem('slack-revealed-global', JSON.stringify(newRevealed));
                                  }}
                                >
                                  {revealed[`raw_result-${selectedChannel}-${index}`] ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <span className="text-gray-600 text-xs">
                              {timestamp ? new Date(parseFloat(timestamp) * 1000).toLocaleString() : 'Unknown'}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center space-x-2">
                              {!isResolved && (
                                <button
                                  className={`px-3 py-1 text-xs rounded transition-colors ${
                                    resolvingAlerts.has(alertId) || selectedCount > 0
                                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                      : 'bg-green-200 text-green-700 hover:bg-green-300'
                                  }`}
                                  onClick={handleResolveToggle}
                                  disabled={resolvingAlerts.has(alertId) || selectedCount > 0}
                                >
                                  {resolvingAlerts.has(alertId) ? 'Resolving...' : 'Resolve'}
                                </button>
                              )}
                              {isResolved && (
                                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                  Resolved
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                        {secret && revealed[`raw_result-${selectedChannel}-${index}`] && (
                          <tr className="bg-gray-50">
                            <td colSpan={9} className="p-3">
                              <div className="font-mono text-xs text-gray-900 break-all">{secret}</div>
                            </td>
                          </tr>
                        )}
                        {hasError && (
                          <tr className="border-b border-red-200 bg-red-50">
                            <td colSpan={9} className="p-3">
                              <div className="flex items-center gap-2 text-red-700">
                                <svg
                                  className="w-4 h-4 text-red-600 flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                  />
                                </svg>
                                <span className="text-sm font-medium">Error:</span>
                                <span className="text-sm">{rowErrors[rowId]}</span>
                                <button
                                  onClick={() => clearRowErrors([rowId])}
                                  className="ml-auto text-red-500 hover:text-red-700 focus:outline-none"
                                  aria-label="Dismiss error"
                                >
                                  <EyeOff className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {filteredFindings.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-900 text-lg mb-4">
                    {filterType === 'all' 
                      ? 'No findings found for this channel'
                      : `No ${filterType==='verified'? 'active' : filterType==='resolved' ? 'resolved' : 'unvalidated'} findings found for this channel`}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {filterType === 'all' 
                      ? 'This channel appears to be clean of any detected secrets.'
                      : 'Try changing the filter to see other findings.'}
                  </p>
                </div>
              )}

              {/* Bulk Actions Bar removed; now integrated into filter bar for consistency */}

              {/* Pagination Controls */}
              <div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4">
                <div className="text-sm text-[#8D82AA]">
                  Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} results
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={!pagination.has_previous}
                      className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                        !pagination.has_previous
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-[#C0ACF3] text-black hover:bg-[#a088ea]"
                      }`}
                    >
                      Previous
                    </button>
                    <span className="text-sm text-[#8D82AA]">
                      Page {pagination.page} of {pagination.last_page}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={!pagination.has_next}
                      className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                        !pagination.has_next
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-[#C0ACF3] text-black hover:bg-[#a088ea]"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-[#8D82AA]">Per page:</label>
                    <select
                      value={pagination.per_page}
                      onChange={(e) => handlePerPageChange(Number(e.target.value))}
                      className="px-3 py-2 rounded text-sm bg-[#F4F0FD] text-[#8D82AA] border border-[#C0ACF3] focus:outline-none"
                    >
                      {[10, 5, 20, 30, 50].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const SlackPage = () => {
  const navigate = useNavigate()
  const [channels, setChannels] = useState(() => {
    // Load channels from localStorage on component mount
    const saved = localStorage.getItem('slack-channels');
    return saved ? JSON.parse(saved) : [];
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showChannelModal, setShowChannelModal] = useState(false)
  const [liveChannels, setLiveChannels] = useState([])
  const [selectedLiveChannels, setSelectedLiveChannels] = useState([])
  const [scanningChannels, setScanningChannels] = useState(false)
  const [channelSearchQuery, setChannelSearchQuery] = useState('')
  
  // Findings state (similar to S3)
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [findings, setFindings] = useState([])
  const [findingsLoading, setFindingsLoading] = useState(false)
  const [filterType, setFilterType] = useState('all')
  const [channelCounts, setChannelCounts] = useState(() => {
    // Load channel counts from localStorage on component mount
    const saved = localStorage.getItem('slack-channel-counts');
    return saved ? JSON.parse(saved) : {};
  })
  const [revealed, setRevealed] = useState(() => {
    // Load revealed state from localStorage on component mount
    const saved = localStorage.getItem('slack-revealed-global');
    return saved ? JSON.parse(saved) : {};
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
    has_next: false,
    has_previous: false,
    last_page: 1
  })
  const [resolvingAlerts, setResolvingAlerts] = useState(new Set())
  // eslint-disable-next-line
  const [scannedChannels, setScannedChannels] = useState([])
  const [pollingInterval, setPollingInterval] = useState(null)
  const [isPolling, setIsPolling] = useState(false)
  // eslint-disable-next-line
  const [channelStatuses, setChannelStatuses] = useState({})
  const [channelPollingIntervals, setChannelPollingIntervals] = useState({})
  
  const { showToast } = useToaster()

  // Fetch counts for a specific channel
  const fetchChannelCounts = useCallback(async (channelId) => {
    try {
      const response = await axios.get(
        `${ENV.REACT_APP_URL}api/slack/alerts/${channelId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      )
      
      const alertsData = response.data.alerts || response.data.data || response.data || []
      const alerts = Array.isArray(alertsData) ? alertsData : []
      const activeCount = alerts.filter(alert => alert.Verified || alert.verified).length
      const unvalidatedCount = alerts.filter(alert => !(alert.Verified || alert.verified)).length
      const resolvedCount = alerts.filter(alert => alert.Resolved || alert.resolved).length
      const unresolvedCount = alerts.filter(alert => !(alert.Resolved || alert.resolved)).length
      
      setChannelCounts(prev => {
        const newCounts = {
          ...prev,
          [channelId]: {
            active: activeCount,
            unvalidated: unvalidatedCount,
            resolved: resolvedCount,
            unresolved: unresolvedCount,
            total: alerts.length
          }
        };
        localStorage.setItem('slack-channel-counts', JSON.stringify(newCounts));
        return newCounts;
      })
      
      return { active: activeCount, unvalidated: unvalidatedCount, resolved: resolvedCount, unresolved: unresolvedCount, total: alerts.length }
    } catch (error) {
      console.error('Error fetching channel counts:', error)
      return { active: 0, unvalidated: 0, resolved: 0, unresolved: 0, total: 0 }
    }
  }, [])

  // Fetch findings for a specific channel
  const fetchChannelFindings = async (channelId, page = 1, per_page = 10) => {
    try {
      setFindingsLoading(true)
      
      const response = await axios.get(
        `${ENV.REACT_APP_URL}api/slack/alerts/${channelId}?page=${page}&per_page=${per_page}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      )
      
      console.log('Channel findings response:', response.data)
      
      // Handle paginated response - check for both 'alerts' and 'data' keys
      const alertsData = response.data.alerts || response.data.data || response.data
      const alertsArray = Array.isArray(alertsData) ? alertsData : []
      
      console.log('Found alerts data:', alertsArray)
      setFindings(alertsArray)
      
      // Set pagination based on response structure
      setPagination({
        page: response.data.page || page,
        per_page: response.data.per_page || per_page,
        total: response.data.total_count || response.data.total || alertsArray.length,
        has_next: (response.data.page || page) < (response.data.total_pages || response.data.last_page || 1),
        has_previous: (response.data.page || page) > 1,
        last_page: response.data.total_pages || response.data.last_page || 1
      })
    } catch (error) {
      console.error('Error fetching channel findings:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch channel findings'
      showToast(errorMessage, 'error')
    } finally {
      setFindingsLoading(false)
    }
  }

  // Handle channel selection -> navigate to findings page by channel id
  const handleChannelSelect = (channel) => {
    const channelId = channel.id || channel.channel_id
    if (channelId) {
      navigate(`/integration/slack/${channelId}`)
    }
  }

  // Handle back to channels
  const handleBackToChannels = () => {
    setSelectedChannel(null)
    setFindings([])
  }

  // Pagination handlers
  const handlePrevPage = () => {
    if (pagination.has_previous && selectedChannel) {
      fetchChannelFindings(selectedChannel.id, pagination.page - 1, pagination.per_page)
    }
  }

  const handleNextPage = () => {
    if (pagination.has_next && selectedChannel) {
      fetchChannelFindings(selectedChannel.id, pagination.page + 1, pagination.per_page)
    }
  }

  const handlePerPageChange = (newPerPage) => {
    if (selectedChannel) {
      fetchChannelFindings(selectedChannel.id, 1, newPerPage)
    }
  }


  const handleSelectLiveChannel = (channel) => {
    setSelectedLiveChannels((prev) => {
      const channelId = channel.id
      const isSelected = prev.some((c) => c.id === channelId)
      
      if (isSelected) {
        return prev.filter((c) => c.id !== channelId)
      } else {
        return [...prev, channel]
      }
    })
  }

  const handleSelectAllLiveChannels = () => {
    if (selectedLiveChannels.length === liveChannels.length) {
      setSelectedLiveChannels([])
    } else {
      setSelectedLiveChannels(liveChannels)
    }
  }

  // Fetch counts for all channels
  const fetchAllChannelCounts = useCallback(async (channelsList) => {
    const countPromises = channelsList.map(channel => 
      fetchChannelCounts(channel.id).catch(() => ({ active: 0, unvalidated: 0, total: 0 }))
    )
    await Promise.all(countPromises)
  }, [fetchChannelCounts])

  // Load counts for existing channels on component mount
  useEffect(() => {
    if (channels.length > 0) {
      fetchAllChannelCounts(channels)
    }
  }, [channels, fetchAllChannelCounts])


  // Fetch scanned channels and filter unscanned ones
  const fetchScannedChannels = async () => {
    try {
      const response = await axios.get(
        `${ENV.REACT_APP_URL}api/slack/scan-channels`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      )
      
      console.log('Scanned channels response:', response.data)
      const scanned = response.data?.channels || []
      setScannedChannels(scanned)
      
      return scanned
    } catch (error) {
      console.error('Error fetching scanned channels:', error)
      setScannedChannels([])
      return []
    }
  }

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingInterval) {
      clearInterval(pollingInterval)
      setPollingInterval(null)
      setIsPolling(false)
      console.log('Stopped scan status polling')
    }
  }, [pollingInterval])

  // Start polling for scan status updates
  // const startPolling = useCallback(() => {
  //   if (isPolling) return // Already polling
    
  //   setIsPolling(true)
  //   console.log('Starting scan status polling...')
    
  //   const interval = setInterval(async () => {
  //     try {
  //       // Fetch updated scanned channels
  //       const updatedScannedChannels = await fetchScannedChannels()
        
  //       // Fetch updated counts for all channels
  //       if (channels.length > 0) {
  //         await fetchAllChannelCounts(channels)
  //       }
        
  //       // Check if any new channels have been scanned
  //       const previousScannedIds = scannedChannels.map(ch => ch.channel_id)
  //       const newScannedChannels = updatedScannedChannels.filter(
  //         ch => !previousScannedIds.includes(ch.channel_id)
  //       )
        
  //       if (newScannedChannels.length > 0) {
  //         console.log('New channels scanned:', newScannedChannels)
  //         showToast(`${newScannedChannels.length} channel(s) scan completed!`, 'success')
          
  //         // Stop polling after successful scan completion
  //         stopPolling()
  //       }
        
  //     } catch (error) {
  //       console.error('Error during polling:', error)
  //     }
  //   }, 5000) // Poll every 5 seconds
    
  //   setPollingInterval(interval)
  // }, [isPolling, channels, scannedChannels, fetchAllChannelCounts, showToast, stopPolling])

  // Get individual channel status
  const getChannelStatus = async (channelId) => {
    try {
      const response = await axios.get(
        `${ENV.REACT_APP_URL}api/slack/channel-status/${channelId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      )
      return response.data
    } catch (error) {
      console.error('Error fetching channel status:', error)
      return { channel_id: channelId, is_added: false, is_scanned: false, status: 'not_found' }
    }
  }

  // Start individual channel status polling
  const startChannelStatusPolling = useCallback((channelId) => {
    // Clear any existing polling for this channel
    if (channelPollingIntervals[channelId]) {
      clearInterval(channelPollingIntervals[channelId])
    }

    console.log(`Starting status polling for channel ${channelId}`)
    
    const pollInterval = setInterval(async () => {
      try {
        const status = await getChannelStatus(channelId)
        
        // Update channel status
        setChannelStatuses(prev => ({
          ...prev,
          [channelId]: status
        }))
        
        // If scan is complete, stop polling for this channel
        if (status.is_scanned) {
          console.log(`Scan completed for channel ${channelId}`)
          clearInterval(pollInterval)
          setChannelPollingIntervals(prev => {
            const newIntervals = { ...prev }
            delete newIntervals[channelId]
            return newIntervals
          })
          
          // Refresh counts for this channel
          await fetchChannelCounts(channelId)
          
          showToast(`Scan completed for channel ${status.channel_name || channelId}!`, 'success')
        }
        
      } catch (error) {
        console.error(`Error polling status for channel ${channelId}:`, error)
      }
    }, 3000) // Poll every 3 seconds
    
    // Store the interval ID
    setChannelPollingIntervals(prev => ({
      ...prev,
      [channelId]: pollInterval
    }))
  }, [channelPollingIntervals, fetchChannelCounts, showToast])

  // Stop individual channel polling
  // const stopChannelStatusPolling = useCallback((channelId) => {
  //   if (channelPollingIntervals[channelId]) {
  //     clearInterval(channelPollingIntervals[channelId])
  //     setChannelPollingIntervals(prev => {
  //       const newIntervals = { ...prev }
  //       delete newIntervals[channelId]
  //       return newIntervals
  //     })
  //     console.log(`Stopped status polling for channel ${channelId}`)
  //   }
  // }, [channelPollingIntervals])

  // Cleanup polling on component unmount
  useEffect(() => {
    return () => {
      stopPolling()
      // Cleanup all individual channel polling
      Object.values(channelPollingIntervals).forEach(interval => {
        clearInterval(interval)
      })
    }
  }, [stopPolling, channelPollingIntervals])

  // Fetch all channels and filter out scanned ones
  const fetchAllChannelsAndFilter = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Fetch both scanned channels and all channels
      const [scannedChannelsData, allChannelsResponse] = await Promise.all([
        fetchScannedChannels(),
        axios.get(
          `${ENV.REACT_APP_URL}api/slack/channels`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
            },
          }
        )
      ])
      
      console.log('All channels response:', allChannelsResponse.data)
      const allChannels = allChannelsResponse.data.channels || []
      
      // Get scanned channel IDs (only those actually scanned)
      const scannedChannelIds = new Set(
        (scannedChannelsData || [])
          .filter((channel) => channel.is_scanned === true)
          .map((channel) => channel.channel_id)
      )
      
      // Get already added channel IDs
      const addedChannelIds = new Set(channels.map(channel => channel.id))
      
      // Filter: keep only channels that are not added yet, not scanned, and where we are a member
      const unscanned = allChannels.filter(channel => 
        !addedChannelIds.has(channel.id) &&
        !scannedChannelIds.has(channel.id) &&
        channel.is_member === true
      )
      
      console.log('Unscanned channels:', unscanned)
      setLiveChannels(unscanned)
      // Always open modal; it will show an empty state if none
      setShowChannelModal(true)
      
    } catch (error) {
      console.error('Error fetching channels:', error)
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch channels'
      setError(errorMessage)
      showToast(errorMessage, 'error')
    } finally {
      setLoading(false)
    }
  }

  // Add channels without scanning them using new API
  const addSelectedChannels = async () => {
    if (selectedLiveChannels.length === 0) {
      showToast('No channels selected. Please select channels first.', 'error')
      return
    }

    try {
      setScanningChannels(true)
      
      // Add each channel individually using the new API
      const addPromises = selectedLiveChannels.map(async (channel) => {
        const response = await axios.post(
          `${ENV.REACT_APP_URL}api/slack/add-channel`,
          {
            channel_id: channel.id
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
            },
          }
        )
        return response.data
      })
      
      const results = await Promise.all(addPromises)
      console.log('Add channel results:', results)
      
      // Add channels to local channels state
      const newChannels = [...channels, ...selectedLiveChannels]
      setChannels(newChannels)
      localStorage.setItem('slack-channels', JSON.stringify(newChannels))
      
      showToast(`Successfully added ${selectedLiveChannels.length} channel${selectedLiveChannels.length !== 1 ? 's' : ''} for monitoring!`, 'success')
      
      // Close modal and reset selection
      setShowChannelModal(false)
      setSelectedLiveChannels([])
      
    } catch (error) {
      console.error('Error adding channels:', error)
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to add selected channels'
      showToast(errorMessage, 'error')
    } finally {
      setScanningChannels(false)
    }
  }

  // Add and scan selected channels using new API
  const scanSelectedChannels = async () => {
    if (selectedLiveChannels.length === 0) {
      showToast('No channels selected for scanning. Please select channels first.', 'error')
      return
    }

    try {
      setScanningChannels(true)
      
      // Add and scan each channel individually using the new API
      const addScanPromises = selectedLiveChannels.map(async (channel) => {
        const response = await axios.post(
          `${ENV.REACT_APP_URL}api/slack/add-and-scan-channel`,
          {
            channel_id: channel.id
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
            },
          }
        )
        return response.data
      })
      
      const results = await Promise.all(addScanPromises)
      console.log('Add and scan results:', results)
      
      // Add channels to local channels state
      const newChannels = [...channels, ...selectedLiveChannels]
      setChannels(newChannels)
      localStorage.setItem('slack-channels', JSON.stringify(newChannels))
      
      showToast(`Successfully added and started scanning ${selectedLiveChannels.length} channel${selectedLiveChannels.length !== 1 ? 's' : ''}!`, 'success')
      
      // Start individual channel status polling for each channel
      selectedLiveChannels.forEach(channel => {
        startChannelStatusPolling(channel.id)
      })
      
      // Close modal and reset selection
      setShowChannelModal(false)
      setSelectedLiveChannels([])
      
    } catch (error) {
      console.error('Error starting add and scan:', error)
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to add and scan channels'
      showToast(errorMessage, 'error')
    } finally {
      setScanningChannels(false)
    }
  }


  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3">
      <div className="mx-auto">
        {/* Breadcrumb + Page Title */}
        <div className="mb-8 space-y-2">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <a href="/">Home</a>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <a href="/identity-management">Identity Management</a>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Slack</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <h1 className="text-black text-2xl md:text-3xl font-bold">Slack Security Analysis</h1>
            {isPolling && (
              <div className="ml-2 flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-blue-700 text-sm">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Monitoring scans...</span>
              </div>
            )}
          </div>
        </div>

        {/* Toolbar: search + scan button */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
          <div className="w-full lg:w-1/2 max-w-[640px]">
            <div className="relative">
              <input
                type="text"
                value={channelSearchQuery}
                onChange={(e) => setChannelSearchQuery(e.target.value)}
                placeholder="Search channels on this page"
                className="w-full pl-10 pr-3 py-2 rounded border border-[#441a9a] bg-white text-[#000000] placeholder-[#8D82AA] focus:outline-none focus:border-[#441a9a] focus:ring-2 focus:ring-[#441a9a] focus:ring-offset-0 transition-colors"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8D82AA]" />
            </div>
          </div>
          <div className="flex gap-3 flex-wrap items-center justify-end w-full lg:w-auto">
            <button
              onClick={fetchAllChannelsAndFilter}
              disabled={loading}
              className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium border transition-all duration-200 shadow-sm ${
                loading
                  ? 'bg-gray-400 text-gray-200 border-gray-400 cursor-not-allowed'
                  : 'bg-[#441a9a] text-white border-[#441a9a] hover:bg-[#3a1080] hover:shadow-md'
              }`}
            >
              {loading ? (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              {loading ? 'Loading...' : 'Scan Channel'}
            </button>
          </div>
        </div>

        {/* Shimmer loading state */}
        {loading && channels.length === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 mb-6">
            {[...Array(8)].map((_, index) => (
              <Card key={index} className="h-full relative overflow-hidden bg-[#F9F9F9] flex flex-col rounded-xl">
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
                <CardHeader className="py-2 px-3 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 bg-gray-200 rounded" />
                      <div className="h-4 w-24 bg-gray-200 rounded" />
                    </div>
                    <div className="h-4 w-4 bg-transparent" />
                  </div>
                </CardHeader>
                <CardContent className="mt-2 pb-2 px-3 flex-1 flex items-center">
                  <div className="space-y-3 w-full">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <div className="h-2 w-2 rounded-full bg-gray-300" />
                      </div>
                      <div className="h-3 w-40 bg-gray-200 rounded" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <div className="h-2 w-2 rounded-full bg-gray-300" />
                      </div>
                      <div className="h-3 w-24 bg-gray-200 rounded" />
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="px-3 py-2">
                  <div className="flex items-center justify-between gap-2 w-full">
                    <div className="h-6 w-24 bg-gray-200 rounded" />
                    <div className="h-6 w-20 bg-gray-200 rounded" />
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-600 text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Content based on state */}
        {selectedChannel ? (
          <SlackFindingsDisplay
            channel={selectedChannel}
            findings={findings}
            loading={findingsLoading}
            onBack={handleBackToChannels}
            selectedChannel={selectedChannel.id}
            fetchChannelFindings={fetchChannelFindings}
            pagination={pagination}
            handlePrevPage={handlePrevPage}
            handleNextPage={handleNextPage}
            handlePerPageChange={handlePerPageChange}
            filterType={filterType}
            setFilterType={setFilterType}
            revealed={revealed}
            setRevealed={setRevealed}
            showToast={showToast}
            resolvingAlerts={resolvingAlerts}
            setResolvingAlerts={setResolvingAlerts}
            setFindings={setFindings}
          />
        ) : loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 mb-6">
            {[...Array(8)].map((_, index) => (
              <Card key={index} className="h-full relative overflow-hidden bg-[#F9F9F9] flex flex-col rounded-xl">
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
                <CardHeader className="py-2 px-3 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 bg-gray-200 rounded" />
                      <div className="h-4 w-24 bg-gray-200 rounded" />
                    </div>
                    <div className="h-4 w-4 bg-transparent" />
                  </div>
                </CardHeader>
                <CardContent className="mt-2 pb-2 px-3 flex-1 flex items-center">
                  <div className="space-y-3 w-full">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <div className="h-2 w-2 rounded-full bg-gray-300" />
                      </div>
                      <div className="h-3 w-40 bg-gray-200 rounded" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <div className="h-2 w-2 rounded-full bg-gray-300" />
                      </div>
                      <div className="h-3 w-24 bg-gray-200 rounded" />
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="px-3 py-2">
                  <div className="flex items-center justify-between gap-2 w-full">
                    <div className="h-6 w-24 bg-gray-200 rounded" />
                    <div className="h-6 w-20 bg-gray-200 rounded" />
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : channels.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-[#C0ACF3] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-black mb-2">No Channels Selected</h3>
            <p className="text-gray-600 mb-6">Click "Scan New Channel" to select channels from your workspace</p>
          </div>
        ) : channels.length === 0 ? (
          <Card className="w-full">
            <CardContent className="text-center py-16">
              <div className="w-24 h-24 bg-[#C0ACF3] rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">No Slack Channels Added</h3>
              <p className="text-gray-600 mb-6">Fetch channels from your workspace to start monitoring.</p>
              <button
                onClick={fetchAllChannelsAndFilter}
                disabled={loading}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded text-sm font-medium border transition-all duration-200 shadow-sm ${
                  loading
                    ? 'bg-gray-400 text-gray-200 border-gray-400 cursor-not-allowed'
                    : 'bg-[#441a9a] text-white border-[#441a9a] hover:bg-[#3a1080] hover:shadow-md'
                }`}
              >
                {loading ? (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {loading ? 'Loading...' : 'Scan Channel'}
              </button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">

            {/* Filter channels by search */}
            {(() => {
              const q = channelSearchQuery.trim().toLowerCase();
              const filteredChannels = q.length > 0
                ? channels.filter((c) =>
                    (c.name || '').toLowerCase().includes(q)
                  )
                : channels;
              if (filteredChannels.length === 0) {
                return (
                  <div className="w-full border border-[#E5E7EB] rounded-xl bg-[#FAFAFA] p-6 text-center text-gray-600">
                    <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1010.5 18a7.5 7.5 0 006.15-3.35z" />
                      </svg>
                    </div>
                    <div className="text-sm">No channels found for your search.</div>
                  </div>
                );
              }
              return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
              {filteredChannels.map((channel) => (
                <Card
                  key={channel.id}
                  onClick={() => handleChannelSelect(channel)}
                  className="h-full hover:shadow-lg transition-all duration-200 cursor-pointer bg-[#F9F9F9] flex flex-col rounded-xl"
                >
                  <CardHeader className="py-2 px-3 border-b">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <img src="/settingspage/slack.png" alt="Slack" className="h-4 w-4 flex-shrink-0" />
                        <span className="relative group inline-flex items-center min-w-0">
                          <h1 className="text-md font-semibold text-foreground truncate max-w-[140px] sm:max-w-[180px] md:max-w-[220px] lg:max-w-[260px]">
                            {channel.is_im ? channel.name : `#${channel.name}`}
                          </h1>
                          <span className="pointer-events-none absolute left-0 sm:left-1/2 sm:-translate-x-1/2 top-full mt-2 z-20 hidden group-hover:block whitespace-normal break-words rounded-md bg-white border border-gray-200 shadow-lg text-[11px] text-gray-900 px-2 py-1 max-w-[80vw] sm:max-w-[320px]">
                            {channel.is_im ? channel.name : `#${channel.name}`}
                          </span>
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">Slack Channel</div>
                    </div>
                  </CardHeader>

                  <CardContent className="mt-2 pb-2 px-3 flex-1 flex items-center">
                    <div className="space-y-3 w-full">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <div className={`h-2 w-2 rounded-full ${channel.is_private ? 'bg-purple-500' : channel.is_im ? 'bg-blue-500' : channel.is_mpim ? 'bg-indigo-500' : 'bg-green-500'}`}></div>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {channel.is_im ? 'Direct Message' : channel.is_mpim ? 'Group DM' : channel.is_private ? 'Private' : 'Public'}{channel.is_member ? ' • Member' : ' • Not a member'}{channel.num_members ? ` • ${channel.num_members} members` : ''}{channel.is_archived ? ' • Archived' : ''}
                        </span>
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter className="px-3 py-2">
                    <div className="flex items-center justify-between gap-2 w-full">
                      <div className="bg-gray-100 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                        <span className="text-xs font-medium text-foreground">
                          Findings {channelCounts[channel.id]?.total || ((channelCounts[channel.id]?.active || 0) + (channelCounts[channel.id]?.unvalidated || 0))}
                        </span>
                      </div>
                      {(channelCounts[channel.id]?.active || 0) > 0 ? (
                        <div className="px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]" style={{ background: '#CCACEA61' }}>
                          <span className="text-xs font-medium text-black">
                            Active {channelCounts[channel.id]?.active || 0}
                          </span>
                        </div>
                      ) : (
                        <div className="bg-green-100 border border-green-200 px-3 py-1 rounded text-center flex items-center justify-center min-h-[28px]">
                          <span className="text-xs font-medium text-green-700">Secure</span>
                        </div>
                      )}
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
              );
            })()}
          </div>
        )}


        {/* Modal for Adding New Slack Channels */}
        <SelectUnscannedChannelsModal
          open={showChannelModal}
          onClose={() => setShowChannelModal(false)}
          liveChannels={liveChannels}
          selectedLiveChannels={selectedLiveChannels}
          handleSelectLiveChannel={handleSelectLiveChannel}
          handleSelectAllLiveChannels={handleSelectAllLiveChannels}
          addSelectedChannels={addSelectedChannels}
          scanSelectedChannels={scanSelectedChannels}
          scanningChannels={scanningChannels}
        />
      </div>
    </div>
  )
}

export default SlackPage
